import SystemUpdateAltRoundedIcon from "@mui/icons-material/SystemUpdateAltRounded";
import { Box, Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import { triggerDesktopUpdateCheck } from "../../../lib/notifications/desktopBridge.js";

/**
 * Inline "Update now" action on the desktop-release announcement (desktop app
 * only — the caller gates on `isTauri()`). Clicking it dispatches the SAME
 * `menu://check-update` event as the tray "Check for Updates…" item, so the
 * central `<UpdateBanner/>` takes over with the full check → download →
 * restart flow. We flip the label to "Checking…" briefly so the click is
 * acknowledged even before that modal appears.
 */
export function UpdateNowButton({ version }: { version: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  // Re-arm after a short window so a "you're already up to date" result (which
  // only flashes a corner toast) doesn't leave the button stuck on "Checking…".
  useEffect(() => {
    if (!busy) return undefined;
    const id = setTimeout(() => setBusy(false), 6000);
    return () => clearTimeout(id);
  }, [busy]);
  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        variant="contained"
        disabled={busy}
        data-testid="chat-update-now"
        startIcon={
          busy ? (
            <CircularProgress size={14} color="inherit" aria-hidden />
          ) : (
            <SystemUpdateAltRoundedIcon sx={{ fontSize: 16 }} aria-hidden />
          )
        }
        onClick={() => {
          setBusy(true);
          void triggerDesktopUpdateCheck();
        }}
        aria-label={`Update the desktop app to version ${version}`}
        sx={{ textTransform: "none", borderRadius: 1, boxShadow: "none" }}
      >
        {busy ? "Checking…" : "Update now"}
      </Button>
    </Box>
  );
}
